import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContentLayout from "../../component/ContentLayout";
import CustomButton from "../../component/Button";
import { VscLoading } from "react-icons/vsc";
import { MdOutlinePolicy, MdVerifiedUser, MdDraw } from "react-icons/md";
import EditIcon from "@mui/icons-material/Edit";
import {
  ADMIN_SIGNATURE_SECTION_ID,
  ACKNOWLEDGEMENT_SECTION_ID,
  buildCompanySafetyPolicySections,
  buildDefaultAdminSignatureSection,
  formatCompanySignatureLine,
  ONMYWAY_PLATFORM_TAGLINE,
  ONMYWAY_SAFETY_FEATURES,
  stripPolicySignatureLines,
  type PolicySection,
} from "../../util/companySafetyPolicyContent";
import { downloadCompanySafetyPolicyPdf } from "../../util/policyPdfExport";
import { getCompanyDisplayName } from "../../util/companyDisplayName";
import { fetchCompanyDashboard } from "../../util/fetchCompanyDashboard";
import { setCompanyData } from "../../store/reducers/companySlice";
import {
  ensureCompanySafetyPolicyRecord,
  fetchCompanySafetyPolicy,
  PolicyVersionConflictError,
  saveAdminSignatureSection,
  saveCompanySafetyPolicySections,
  signCompanySafetyPolicy,
  type CompanySafetyPolicyRecord,
} from "../../util/companySafetyPolicyApi";
import SectionEditModal from "./SectionEditModal";
import "../../styles/companySafetyPolicy.css";

const CompanySafetyPolicy = () => {
  const dispatch = useDispatch();
  const companyData = useSelector((state: any) => state?.company?.companyData);
  const [downloading, setDownloading] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [policyRecord, setPolicyRecord] = useState<CompanySafetyPolicyRecord | null>(null);
  const [sections, setSections] = useState<PolicySection[]>([]);
  const [adminSignatureSection, setAdminSignatureSection] = useState<PolicySection>(
    () => buildDefaultAdminSignatureSection("Your Company")
  );
  const [editingSection, setEditingSection] = useState<PolicySection | null>(null);
  const [savingSection, setSavingSection] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signAcknowledged, setSignAcknowledged] = useState(false);
  const [signError, setSignError] = useState("");

  const companyName = useMemo(
    () => getCompanyDisplayName(companyData) || "Your Company",
    [companyData]
  );

  const defaultSections = useMemo(
    () => buildCompanySafetyPolicySections(companyName),
    [companyName]
  );

  const loadCompanyData = useCallback(async () => {
    setLoadingCompany(true);
    try {
      const data = await fetchCompanyDashboard();
      if (data) dispatch(setCompanyData(data));
    } catch (error) {
      console.error("Error loading company data:", error);
    } finally {
      setLoadingCompany(false);
    }
  }, [dispatch]);

  const loadPolicy = useCallback(async () => {
    setLoadingPolicy(true);
    try {
      const saved = await fetchCompanySafetyPolicy(companyName);
      setPolicyRecord(saved);
      // GET data: null → keep frontend defaults (not seeded by backend)
      setSections(
        saved?.sections ?? buildCompanySafetyPolicySections(companyName)
      );
      setAdminSignatureSection(
        saved?.adminSignatureSection ??
          buildDefaultAdminSignatureSection(companyName)
      );
    } catch (error) {
      console.error("Error loading safety policy:", error);
      setPolicyRecord(null);
      setSections(buildCompanySafetyPolicySections(companyName));
      setAdminSignatureSection(buildDefaultAdminSignatureSection(companyName));
    } finally {
      setLoadingPolicy(false);
    }
  }, [companyName]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  useEffect(() => {
    if (!policyRecord?.sections?.length) {
      setSections(defaultSections);
    }
    if (!policyRecord?.adminSignatureSection) {
      setAdminSignatureSection(buildDefaultAdminSignatureSection(companyName));
    }
  }, [
    companyName,
    defaultSections,
    policyRecord?.sections?.length,
    policyRecord?.adminSignatureSection,
  ]);

  const isSigned = Boolean(
    policyRecord?.signature &&
      policyRecord.signature.policyVersion === policyRecord.version
  );

  const handleDownload = async () => {
    if (!isSigned) return;

    setDownloading(true);
    try {
      const signed = policyRecord!.signature!;

      await downloadCompanySafetyPolicyPdf(companyName, {
        sections,
        adminSignatureSection,
        signature: signed,
        policyVersion: policyRecord?.version,
      });
    } catch (error) {
      console.error("Error downloading safety policy:", error);
    } finally {
      setDownloading(false);
    }
  };

  const openSectionEditor = (section: PolicySection) => {
    if (section.id === ACKNOWLEDGEMENT_SECTION_ID) {
      setEditingSection({
        ...section,
        paragraphs: stripPolicySignatureLines(section.paragraphs),
      });
      return;
    }
    setEditingSection(section);
  };

  const handleSaveSection = async (updated: PolicySection) => {
    setSavingSection(true);
    try {
      if (updated.id === ADMIN_SIGNATURE_SECTION_ID) {
        const record = await saveAdminSignatureSection(updated, companyName);
        setPolicyRecord(record);
        setAdminSignatureSection(record.adminSignatureSection);
        setEditingSection(null);
        return;
      }

      const cleaned =
        updated.id === ACKNOWLEDGEMENT_SECTION_ID
          ? {
              ...updated,
              paragraphs: stripPolicySignatureLines(updated.paragraphs),
            }
          : updated;

      // Always send full sections array — first PUT creates the row
      const nextSections = sections.map((s) =>
        s.id === cleaned.id ? cleaned : s
      );
      const record = await saveCompanySafetyPolicySections(
        nextSections,
        companyName
      );
      setPolicyRecord(record);
      setSections(record.sections);
      setAdminSignatureSection(record.adminSignatureSection);
      setEditingSection(null);
    } catch (error) {
      console.error("Error saving policy section:", error);
    } finally {
      setSavingSection(false);
    }
  };

  const handleSign = async () => {
    setSignError("");
    const name = signerName.trim();
    if (!name) {
      setSignError("Enter your full name to sign.");
      return;
    }
    if (!signAcknowledged) {
      setSignError("Please confirm you have reviewed and approve this policy.");
      return;
    }
    setSigning(true);
    try {
      // No saved row yet → PUT full frontend defaults/sections first (creates)
      const published = await ensureCompanySafetyPolicyRecord(
        sections,
        policyRecord,
        companyName
      );
      if (!policyRecord) {
        setPolicyRecord(published);
      }

      const record = await signCompanySafetyPolicy(
        {
          signerName: name,
          signerTitle: signerTitle.trim() || undefined,
          policyVersion: published.version,
        },
        companyName
      );
      setPolicyRecord(record);
      setSections(record.sections);
      setAdminSignatureSection(record.adminSignatureSection);
      setSignerName("");
      setSignerTitle("");
      setSignAcknowledged(false);
    } catch (error) {
      console.error("Error signing policy:", error);
      if (error instanceof PolicyVersionConflictError) {
        setSignError(
          error.message || "Policy was updated. Reloading — please sign again."
        );
        await loadPolicy();
        return;
      }
      const message =
        (error as any)?.response?.data?.message ||
        "Unable to sign policy. Try again.";
      setSignError(message);
    } finally {
      setSigning(false);
    }
  };

  const formattedSignedAt = policyRecord?.signature?.signedAt
    ? new Date(policyRecord.signature.signedAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

  const activeSignature = isSigned ? policyRecord?.signature ?? null : null;
  const adminIntro =
    adminSignatureSection.paragraphs[0] ??
    `As company administrator, sign below to attest that you have reviewed and approve this Company Safety Policy for ${companyName}.`;
  const adminSignedNote =
    adminSignatureSection.paragraphs[1] ??
    "This attestation confirms the company administrator has reviewed and approved the published safety policy.";
  const adminCheckboxText =
    adminSignatureSection.paragraphs[2] ??
    "I confirm I have reviewed this policy and authorize its publication for our organization.";

  return (
    <ContentLayout className="items-start rounded-3xl m-2 gap-6 overflow-y-scroll">
      <div className="company-policy-page">
        <div className="company-policy-hero">
          <div className="company-policy-hero__download">
            <CustomButton
              title={
                downloading ? (
                  <VscLoading className="animate-spin text-xl" />
                ) : (
                  <span className="flex items-center gap-2">
                    <MdOutlinePolicy />
                    Download Policy
                  </span>
                )
              }
              onClick={handleDownload}
              disabled={downloading || !isSigned}
              className="!py-2.5 !px-5"
            />
            {!isSigned ? (
              <p className="company-policy-hero__download-hint">
                Sign the policy below to enable download
              </p>
            ) : null}
          </div>
          <div className="company-policy-hero__top">
            <span className="company-policy-hero__badge">
              <MdVerifiedUser />
              Employer Safety Policy
            </span>
          </div>
          <h1 className="company-policy-hero__title">Company Safety Policy</h1>
          <p className="company-policy-hero__company">
            {loadingCompany && !getCompanyDisplayName(companyData) ? (
              <span className="inline-flex items-center gap-2 opacity-70">
                <VscLoading className="animate-spin" /> Loading organization…
              </span>
            ) : (
              companyName
            )}
          </p>
          <p className="company-policy-hero__sub">
            {ONMYWAY_PLATFORM_TAGLINE} Supports OSHA-informed training, federal
            workplace alignment, insurance documentation, and FARS/DFARS readiness.
            Customize with your legal counsel before distribution.
          </p>
        </div>

        <div className="company-policy-intro">
          <p>
            This policy is designed for employers adopting OnMyWay (
            <a
              href="https://onmway.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline"
            >
              onmway.com
            </a>
            ). It links to the <strong>Safety Policy Acknowledgement</strong>{" "}
            employee certification and company readiness documentation in your
            dashboard.
          </p>
        </div>

        <section className="company-policy-features">
          <h2 className="company-policy-features__title">
            10 Protection Features (onmway.com)
          </h2>
          <p className="company-policy-features__sub">
            Complete protection from physical threats, digital threats, and
            compliance failures—powered by advanced AI and live emergency support.
          </p>
          <div className="company-policy-features__grid">
            {ONMYWAY_SAFETY_FEATURES.map((feature) => (
              <article key={feature.name} className="company-policy-feature-card">
                <p className="company-policy-feature-card__name">{feature.name}</p>
                <p className="company-policy-feature-card__tag">{feature.tagline}</p>
                <p className="company-policy-feature-card__desc">
                  {feature.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        {loadingPolicy ? (
          <div className="company-policy-loading">
            <VscLoading className="animate-spin text-2xl text-blue-400" />
            <span>Loading policy…</span>
          </div>
        ) : (
          sections.map((section) => {
            const displayParagraphs =
              section.id === ACKNOWLEDGEMENT_SECTION_ID
                ? stripPolicySignatureLines(section.paragraphs)
                : section.paragraphs;

            return (
              <section key={section.id} className="company-policy-section">
                <div className="company-policy-section__header">
                  <h2 className="company-policy-section__title">{section.title}</h2>
                  <button
                    type="button"
                    className="company-policy-section__edit"
                    onClick={() => openSectionEditor(section)}
                    title={`Edit ${section.title}`}
                  >
                    <EditIcon fontSize="small" />
                    Edit
                  </button>
                </div>
                <div className="company-policy-section__body">
                  {displayParagraphs.map((paragraph, idx) => (
                    <p key={idx} className="company-policy-section__paragraph">
                      {paragraph}
                    </p>
                  ))}
                  {section.id === ACKNOWLEDGEMENT_SECTION_ID ? (
                    <p
                      className={
                        activeSignature
                          ? "company-policy-section__signature-line company-policy-section__signature-line--signed"
                          : "company-policy-section__signature-line"
                      }
                    >
                      {formatCompanySignatureLine(activeSignature)}
                    </p>
                  ) : null}
                  {section.bullets && section.bullets.length > 0 ? (
                    <ul className="company-policy-section__list">
                      {section.bullets.map((bullet, idx) => (
                        <li key={idx} className="company-policy-section__list-item">
                          {bullet.label ? (
                            <>
                              <strong>{bullet.label}:</strong> {bullet.text}
                            </>
                          ) : (
                            bullet.text
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </section>
            );
          })
        )}

        <section className="company-policy-signature">
          <div className="company-policy-section__header">
            <h2 className="company-policy-section__title">
              {adminSignatureSection.title}
            </h2>
            <div className="company-policy-section__header-actions">
              <button
                type="button"
                className="company-policy-section__edit"
                onClick={() => openSectionEditor(adminSignatureSection)}
                title={`Edit ${adminSignatureSection.title}`}
              >
                <EditIcon fontSize="small" />
                Edit
              </button>
            {isSigned ? (
              <span className="company-policy-signature__badge company-policy-signature__badge--signed">
                <MdVerifiedUser />
                Signed
              </span>
            ) : (
              <span className="company-policy-signature__badge company-policy-signature__badge--pending">
                Signature required
              </span>
            )}
            </div>
          </div>

          {isSigned && policyRecord?.signature ? (
            <div className="company-policy-signature__signed">
              <p className="company-policy-signature__line">
                <strong>Signed by:</strong> {policyRecord.signature.signerName}
                {policyRecord.signature.signerTitle
                  ? `, ${policyRecord.signature.signerTitle}`
                  : ""}
              </p>
              <p className="company-policy-signature__line">
                <strong>Date:</strong> {formattedSignedAt}
              </p>
              <p className="company-policy-signature__line">
                <strong>Policy version:</strong> {policyRecord.version}
              </p>
              <p className="company-policy-signature__note">{adminSignedNote}</p>
            </div>
          ) : (
            <div className="company-policy-signature__form">
              <p className="company-policy-signature__intro">{adminIntro}</p>
              <label className="company-policy-signature__field">
                <span>Full name</span>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Administrator full name"
                  className="company-policy-signature__input"
                />
              </label>
              <label className="company-policy-signature__field">
                <span>Title (optional)</span>
                <input
                  type="text"
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                  placeholder="e.g. HR Director, Safety Officer"
                  className="company-policy-signature__input"
                />
              </label>
              <label className="company-policy-signature__checkbox">
                <input
                  type="checkbox"
                  checked={signAcknowledged}
                  onChange={(e) => setSignAcknowledged(e.target.checked)}
                />
                <span>{adminCheckboxText}</span>
              </label>
              {signError ? (
                <p className="company-policy-signature__error">{signError}</p>
              ) : null}
              <CustomButton
                title={
                  signing ? (
                    <VscLoading className="animate-spin text-xl" />
                  ) : (
                    <span className="flex items-center gap-2">
                      <MdDraw />
                      Sign Policy
                    </span>
                  )
                }
                onClick={handleSign}
                disabled={signing}
                className="!py-2.5 !px-6 !w-auto"
              />
            </div>
          )}
        </section>
      </div>

      <SectionEditModal
        open={Boolean(editingSection)}
        section={editingSection}
        saving={savingSection}
        editableTitle={editingSection?.id === ADMIN_SIGNATURE_SECTION_ID}
        onClose={() => setEditingSection(null)}
        onSave={handleSaveSection}
      />
    </ContentLayout>
  );
};

export default CompanySafetyPolicy;
